import { ElementRef, inject, Injectable, signal, Signal } from "@angular/core";
import { InteractivityChecker } from "@angular/cdk/a11y";
import {
    getFirstTabbableElementAfter,
    IOverlayResultApi,
    LgOverlayService
} from "@logex/framework/ui-core";
import { Overlay } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { LgGlobalShortcutsPopupComponent } from "./lg-global-shortcuts-popup.component";
import { isEventTargetInput } from "@logex/framework/utilities";
import { Subject, takeUntil } from "rxjs";

export type LgGlobalShortcutComponent = "sidebar" | "breadcrumb" | "filters" | "body" | "layouts";
export type LgGlobalShortcutAction = (() => void) | ElementRef<HTMLElement>;

export type LgGlobalShortcutRegistration = {
    altKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    key: string;
    codes?: string[] | undefined;
    action: LgGlobalShortcutAction;
    name: string;
    localizationKey: string | undefined;
    standard: boolean;
    enabled: Signal<boolean>;
};

export type LgGlobalShortcut = {
    altKey?: boolean;
    ctrlKey?: boolean;
    shiftKey?: boolean;
    key: string;
    // note: the codes are optional, for cases when ther key itself is enough due to special handling (like option + 1 on Mac)
    codes?: string[] | undefined;
};

const STANDARD_SHORTCUTS: Record<LgGlobalShortcutComponent, LgGlobalShortcut> = {
    sidebar: { altKey: true, key: "1", codes: ["Digit1", "Numpad1"] },
    breadcrumb: { altKey: true, key: "2", codes: ["Digit2", "Numpad2"] },
    body: { altKey: true, key: "3", codes: ["Digit3", "Numpad3"] },
    filters: { altKey: true, key: "4", codes: ["Digit4", "Numpad4"] },
    layouts: { altKey: true, key: "5", codes: ["Digit5", "Numpad5"] }
};

@Injectable({ providedIn: "root" })
export class LgGlobalShortcutsService {
    private _interactivityChecker = inject(InteractivityChecker);
    private _overlayService = inject(LgOverlayService);
    private _overlay = inject(Overlay);

    private _shortcutTriggered = new Subject<string>();

    /** Stream of names of global shortcuts triggered.
     *
     * Note: this is intended for situations when you want to react to other shortcut (f.ex by hiding your popup). Use the action registration parameter for your own action.
     */
    shortcutTriggered$ = this._shortcutTriggered.asObservable();

    private _shortcuts: LgGlobalShortcutRegistration[] = [];
    private _initialized = false;
    private _overlayInstance: IOverlayResultApi | null = null;
    private _overlayClosed: Subject<void> | null = null;

    registerStandardComponent(
        component: LgGlobalShortcutComponent,
        action: LgGlobalShortcutAction,
        enabled?: Signal<boolean>
    ): () => void {
        const shortcut = STANDARD_SHORTCUTS[component];
        const registration: LgGlobalShortcutRegistration = {
            altKey: shortcut.altKey || false,
            ctrlKey: shortcut.ctrlKey || false,
            shiftKey: shortcut.shiftKey || false,
            key: shortcut.key,
            codes: shortcut.codes,
            action,
            name: component,
            localizationKey: `FW._Shortcuts._Components.${component}`,
            standard: true,
            enabled: enabled ?? signal(true)
        };
        return this._addShortcutRegistration(registration, true);
    }

    registerShortcut(
        shortcut: LgGlobalShortcut,
        action: LgGlobalShortcutAction,
        name: string,
        localizationKey?: string | undefined,
        enabled?: Signal<boolean>
    ): () => void {
        if (this.isStandardComponent(name)) {
            throw new Error(`Shortcut name ${name} is reserved for standard compoonents.`);
        }
        const registration: LgGlobalShortcutRegistration = {
            altKey: shortcut.altKey || false,
            ctrlKey: shortcut.ctrlKey || false,
            shiftKey: shortcut.shiftKey || false,
            key: shortcut.key,
            codes: shortcut.codes ? [...shortcut.codes] : undefined,
            action,
            name,
            localizationKey,
            standard: false,
            enabled: enabled ?? signal(true)
        };
        return this._addShortcutRegistration(registration, false);
    }

    isShortcutRegistered(shortcut: LgGlobalShortcut): boolean {
        return this._shortcuts.some(
            s =>
                s.key === shortcut.key &&
                s.altKey === !!shortcut.altKey &&
                s.ctrlKey === !!shortcut.ctrlKey &&
                s.shiftKey === !!shortcut.shiftKey
        );
    }

    isShortcutNameRegistered(name: string): boolean {
        return this._shortcuts.some(s => s.name === name);
    }

    isStandardComponent(name: string): name is LgGlobalShortcutComponent {
        return Object.keys(STANDARD_SHORTCUTS).includes(name);
    }

    private _addShortcutRegistration(
        registration: LgGlobalShortcutRegistration,
        allowStacking = false
    ): () => void {
        if (!allowStacking) {
            if (this.isShortcutNameRegistered(registration.name)) {
                console.error(`Shortcut with name "${registration.name}" is already registered.`);
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                return () => {};
            }
            if (this.isShortcutRegistered(registration)) {
                console.error(
                    `Shortcut with key "${registration.ctrlKey ? "ctrl+" : ""}${registration.shiftKey ? "shift+" : ""}${registration.altKey ? "alt+" : ""}${registration.key}" is already registered.`
                );
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                return () => {};
            }
        }

        this._shortcuts.unshift(registration);
        this._maybeInitialize();
        return () => {
            const index = this._shortcuts.indexOf(registration);
            if (index > -1) {
                this._shortcuts.splice(index, 1);
            }
        };
    }

    private _maybeInitialize(): void {
        if (this._initialized) return;
        this._initialized = true;
        document.addEventListener("keydown", this._handleKeydown.bind(this));
    }

    private _handleKeydown(event: KeyboardEvent): void {
        // if our popup is showing, assume nothing else is on top
        if (this._overlayService.hasOverlayWithFocusTrap() && !this._overlayInstance) {
            return;
        }
        const key = event.key;
        if (key === "/") {
            // Do not pop up when the key was pressed on input field
            if (isEventTargetInput(event)) {
                return;
            }
            this._showShortcutPopup();
        }

        const registration = this._shortcuts.find(
            s =>
                (s.key === key || s.codes?.includes(event.code)) &&
                s.altKey === event.altKey &&
                s.ctrlKey === event.ctrlKey &&
                s.shiftKey === event.shiftKey
        );

        if (registration) {
            event.preventDefault();
            event.stopPropagation();
            if (!registration.enabled()) return;
            this._hideShortcutPopup();
            this._triggerShortcut(registration);
        }
    }

    private _triggerShortcut(registration: LgGlobalShortcutRegistration): void {
        if (registration.action) {
            this._shortcutTriggered.next(registration.name);
            const action = registration.action;
            if (typeof action === "function") {
                action();
            } else {
                const target = getFirstTabbableElementAfter(
                    action.nativeElement,
                    null,
                    { value: true },
                    false,
                    this._interactivityChecker
                );
                target?.blur();
                target?.focus({ focusVisible: true } as any); // part of standard now, though Chrome doesn't yet support it
            }
        }
    }

    private _showShortcutPopup(): void {
        if (this._overlayInstance) return;

        const toShow = this._shortcuts.filter(s => s.localizationKey);
        if (toShow.length === 0) return;

        const positionStrategy = this._overlay
            .position()
            .global()
            .centerHorizontally()
            .centerVertically();

        this._overlayClosed = new Subject<void>();
        this._overlayInstance = this._overlayService.show({
            onClick: () => this._hideShortcutPopup(),
            hasBackdrop: true,
            positionStrategy,
            focusPostHide: "restore",
            trapFocus: true,
            scrollStrategy: this._overlay.scrollStrategies.reposition({ scrollThrottle: 0 })
        });

        const portal = new ComponentPortal(LgGlobalShortcutsPopupComponent);
        const popup = this._overlayInstance.overlayRef.attach(portal).instance;

        popup
            ._initialize(toShow)
            .pipe(takeUntil(this._overlayClosed!))
            .subscribe({
                next: shortcut => {
                    this._hideShortcutPopup();
                    this._triggerShortcut(shortcut);
                },
                complete: () => {
                    this._hideShortcutPopup();
                }
            });
    }

    protected _hideShortcutPopup(): void {
        if (!this._overlayInstance) return;
        this._overlayClosed!.next();
        // the next above will trigger the call again thanks to the "complete" subscription
        if (this._overlayClosed) {
            this._overlayClosed.complete();
            this._overlayClosed = null;
            this._overlayInstance.hide();
            this._overlayInstance = null;
        }
    }
}
